import { forwardRef, useState } from 'react';
import Avatar from './Avatar';
import classes from './ConversationBubble.module.css';
import Alert from './assets/alert.svg';
import { ReactComponent as Like } from './assets/like.svg';
import { ReactComponent as Dislike } from './assets/dislike.svg';
import { ReactComponent as Copy } from './assets/copy.svg';
import { ReactComponent as CheckMark } from './assets/checkmark.svg';
import { ReactComponent as ReadAloud } from './assets/read_aloud.svg';
import ReactMarkdown from 'react-markdown';
import copy from 'copy-to-clipboard';
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';
import { vscDarkPlus } from 'react-syntax-highlighter/dist/cjs/styles/prism';
import DocsGPT3 from './assets/cute_docsgpt3.svg';
import { Tooltip, Button } from '@mui/material';

// const DisableSourceFE = import.meta.env.VITE_DISABLE_SOURCE_FE || false;
const DisableSourceFE = false;
const apiHost = process.env.REACT_APP_API_URL;

const ConversationBubble = forwardRef(function ConversationBubble(
    { message, type, className, feedback, handleFeedback, sources, isTheLast, isLoading },
    ref
) {
    const [openSource, setOpenSource] = useState(null);
    const [copied, setCopied] = useState(false);
    /** Nội dung block code vừa được copy (để hiển thị "Đã copy" trên đúng block) */
    const [copiedBlockContent, setCopiedBlockContent] = useState(null);

    const handleCopyClick = (text) => {
        copy(text);
        setCopied(true);
        setTimeout(() => setCopied(false), 3000);
    };

    const handleCopyCodeBlock = (codeContent) => {
        const text = typeof codeContent === 'string' ? codeContent : String(codeContent).replace(/\n$/, '');
        copy(text);
        setCopiedBlockContent(text);
        setTimeout(() => setCopiedBlockContent(null), 2000);
    };
    const [isCopyHovered, setIsCopyHovered] = useState(false);
    const [isLikeHovered, setIsLikeHovered] = useState(false);
    const [isDislikeHovered, setIsDislikeHovered] = useState(false);
    const [isReadHovered, setIsReadHovered] = useState(false);
    const [isLikeClicked, setIsLikeClicked] = useState(false);
    const [isDislikeClicked, setIsDislikeClicked] = useState(false);
    const [isReadClicked, setIsReadClicked] = useState(false);
    const [audio, setAudio] = useState(new Audio());

    let bubble;

    if (type === 'QUESTION') {
        bubble = (
            <div ref={ref} className={`flex flex-row-reverse self-end ${className}`}>
                <Avatar
                    className="mt-2 h-10 w-10 flex-shrink-0"
                    avatar={
                        <div className="flex h-full w-full items-center justify-center rounded-full bg-[#1976D2] text-xs font-semibold text-white shadow-sm">
                            YOU
                        </div>
                    }
                />
                <div className="ml-2 mr-10 flex max-w-[85%] items-center rounded-3xl bg-[#1976D2] p-3.5 text-white shadow-sm">
                    <div className="whitespace-pre-wrap break-words">
                        {message ? <ReactMarkdown>{message}</ReactMarkdown> : <span className="opacity-70">(Câu hỏi)</span>}
                    </div>
                </div>
            </div>
        );
    } else {
        bubble = (
            <div ref={ref} className={`flex self-start ${className} group flex-col`}>
                <div className="flex self-start">
                    <Avatar
                        className="mt-2 h-10 w-10 text-xs font-semibold"
                        avatar={<div className="flex h-full w-full items-center justify-center rounded-full bg-[#0B4C8C] text-white">AI</div>}
                    />

                    <div
                        className={`ml-2 mr-4 flex rounded-3xl bg-white p-3.5 shadow-sm border border-gray-200 ${
                            type === 'ERROR'
                                ? 'flex-row items-center rounded-full border border-transparent bg-[#FFE7E7] p-2 py-5 text-sm font-normal text-red-3000  dark:border-red-2000 dark:text-white'
                                : 'flex-col rounded-3xl'
                        }`}
                    >
                        {type === 'ERROR' && <img src={Alert} alt="alert" className="mr-2 inline" />}
                        <div className="max-w-[min(100%,72rem)] whitespace-pre-wrap break-words max-h-[70vh] overflow-y-auto overflow-x-hidden" style={{ contain: 'layout' }}>
                            {isLoading ? (
                                <div className="flex items-center gap-2">
                                    <span className="text-gray-700">{message || 'Đang phân tích tài liệu và tạo câu trả lời'}</span>
                                    <div className={classes.loadingDots} aria-hidden>
                                        <span />
                                        <span />
                                        <span />
                                    </div>
                                </div>
                            ) : (
                            <ReactMarkdown
                                components={{
                                h1({ children }) {
                                    return <h1 className="mb-3 mt-1 text-xl font-bold tracking-tight text-gray-900">{children}</h1>;
                                },
                                h2({ children }) {
                                    return <h2 className="mb-2 mt-4 text-lg font-semibold text-gray-800">{children}</h2>;
                                },
                                h3({ children }) {
                                    return <h3 className="mb-1.5 mt-3 text-base font-semibold text-gray-800">{children}</h3>;
                                },
                                p({ children }) {
                                    return <p className="mb-2 leading-relaxed text-gray-800">{children}</p>;
                                },
                                code({ node, inline, className, children, ...props }) {
                                    const match = /language-(\w+)/.exec(className || '');
                                    const codeContent = String(children).replace(/\n$/, '');
                                    const isBlock = !inline && match;
                                    const justCopied = isBlock && copiedBlockContent === codeContent;

                                    return isBlock ? (
                                        <div className="relative group/codeblock rounded overflow-hidden my-2">
                                            <div className="absolute top-1.5 right-1.5 z-10 opacity-80 group-hover/codeblock:opacity-100 transition-opacity">
                                                <Tooltip title={justCopied ? 'Đã copy' : 'Copy đoạn code'}>
                                                    <Button
                                                        size="small"
                                                        variant="outlined"
                                                        onClick={() => handleCopyCodeBlock(codeContent)}
                                                        sx={{
                                                            minWidth: 'auto',
                                                            py: 0.25,
                                                            px: 1,
                                                            textTransform: 'none',
                                                            bgcolor: 'background.paper',
                                                            '&:hover': { bgcolor: 'action.hover' }
                                                        }}
                                                    >
                                                        {justCopied ? (
                                                            <CheckMark style={{ width: 14, height: 14 }} className="stroke-green-2000" />
                                                        ) : (
                                                            <Copy style={{ width: 14, height: 14 }} className="fill-none" />
                                                        )}
                                                    </Button>
                                                </Tooltip>
                                            </div>
                                            <SyntaxHighlighter PreTag="div" language={match[1]} {...props} style={vscDarkPlus}>
                                                {codeContent}
                                            </SyntaxHighlighter>
                                        </div>
                                    ) : (
                                        <code className={className ? className : ''} {...props}>
                                            {children}
                                        </code>
                                    );
                                },
                                ul({ children }) {
                                    return <ul className={`list-inside list-disc whitespace-normal pl-4 ${classes.list}`}>{children}</ul>;
                                },
                                ol({ children }) {
                                    return (
                                        <ol className={`list-inside list-decimal whitespace-normal pl-4 ${classes.list}`}>{children}</ol>
                                    );
                                }
                            }}
                            >
                                {message}
                            </ReactMarkdown>
                            )}
                        </div>
                        {DisableSourceFE || type === 'ERROR' || isLoading ? null : (
                            <>
                                <span className="mt-3 h-px w-full bg-[#DEDEDE]"></span>
                                <div className="mt-3 flex w-full flex-row flex-wrap items-center justify-start gap-2">
                                    <div className="py-1 text-base font-semibold">Sources:</div>
                                    <div className="flex flex-row flex-wrap items-center justify-start gap-2">
                                        {sources?.map((source, index) => (
                                            <div
                                                key={index}
                                                className={`max-w-fit cursor-pointer rounded-[28px] px-4 py-1 ${
                                                    openSource === index ? 'bg-[#007DFF]' : 'bg-[#D7EBFD] hover:bg-[#BFE1FF]'
                                                }`}
                                                onClick={() => setOpenSource(openSource === index ? null : index)}
                                            >
                                                <p
                                                    className={`truncate text-center text-base font-medium ${
                                                        openSource === index ? 'text-white' : 'text-[#007DFF]'
                                                    }`}
                                                >
                                                    {index + 1}. {source.title.substring(0, 45)}
                                                </p>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            </>
                        )}
                        {/* Các nút hành động đặt ở cuối nội dung hội thoại */}
                        {type !== 'ERROR' && !isLoading && (
                            <>
                                <span className="mt-3 h-px w-full bg-[#DEDEDE]"></span>
                                <div
                                    className={`mt-3 flex w-full flex-row flex-wrap items-center justify-start gap-1 ${
                                        isTheLast ? 'md:visible' : 'md:invisible group-hover:md:visible'
                                    }`}
                                >
                                    <div
                                        className="flex items-center justify-center rounded-full p-1.5"
                                        style={{ backgroundColor: isCopyHovered ? '#EEEEEE' : 'transparent' }}
                                    >
                                        {copied ? (
                                            <CheckMark
                                                className="cursor-pointer stroke-green-2000"
                                                onMouseEnter={() => setIsCopyHovered(true)}
                                                onMouseLeave={() => setIsCopyHovered(false)}
                                            />
                                        ) : (
                                            <Tooltip placement="top" title="Sao chép vào clipboard">
                                                <Button
                                                    size="small"
                                                    variant="outlined"
                                                    startIcon={<Copy className="cursor-pointer fill-none" style={{ width: 16, height: 16 }} />}
                                                    onClick={() => handleCopyClick(message)}
                                                    onMouseEnter={() => setIsCopyHovered(true)}
                                                    onMouseLeave={() => setIsCopyHovered(false)}
                                                    sx={{ textTransform: 'none', minWidth: 'auto', py: 0.25, px: 1 }}
                                                >
                                                    Sao chép
                                                </Button>
                                            </Tooltip>
                                        )}
                                    </div>
                                    <div
                                        className="flex items-center justify-center rounded-full p-1.5"
                                        style={{
                                            backgroundColor: isLikeHovered
                                                ? isLikeClicked ? 'rgba(125, 84, 209, 0.3)' : '#EEEEEE'
                                                : isLikeClicked ? 'rgba(125, 84, 209, 0.3)' : 'transparent'
                                        }}
                                    >
                                        <Tooltip placement="top" title="Câu trả lời tốt">
                                            <Like
                                                className={`cursor-pointer ${
                                                    isLikeClicked || feedback === 'LIKE'
                                                        ? 'fill-white-3000 stroke-purple-30'
                                                        : 'fill-none stroke-gray-4000'
                                                }`}
                                                onClick={() => {
                                                    handleFeedback?.('LIKE');
                                                    setIsLikeClicked(true);
                                                    setIsDislikeClicked(false);
                                                }}
                                                onMouseEnter={() => setIsLikeHovered(true)}
                                                onMouseLeave={() => setIsLikeHovered(false)}
                                            />
                                        </Tooltip>
                                    </div>
                                    <div
                                        className="flex items-center justify-center rounded-full p-1.5"
                                        style={{
                                            backgroundColor: isDislikeHovered
                                                ? isDislikeClicked ? 'rgba(248, 113, 113, 0.3)' : '#EEEEEE'
                                                : isDislikeClicked ? 'rgba(248, 113, 113, 0.3)' : 'transparent'
                                        }}
                                    >
                                        <Tooltip placement="top" title="Câu trả lời chưa tốt">
                                            <Dislike
                                                className={`cursor-pointer ${
                                                    isDislikeClicked || feedback === 'DISLIKE'
                                                        ? 'fill-white-3000 stroke-red-2000'
                                                        : 'fill-none stroke-gray-4000'
                                                }`}
                                                onClick={() => {
                                                    handleFeedback?.('DISLIKE');
                                                    setIsDislikeClicked(true);
                                                    setIsLikeClicked(false);
                                                }}
                                                onMouseEnter={() => setIsDislikeHovered(true)}
                                                onMouseLeave={() => setIsDislikeHovered(false)}
                                            />
                                        </Tooltip>
                                    </div>
                                    <div
                                        className="flex items-center justify-center rounded-full p-1.5"
                                        style={{
                                            backgroundColor: isReadHovered
                                                ? isReadClicked ? 'rgba(248, 113, 113, 0.3)' : '#EEEEEE'
                                                : isReadClicked ? 'rgba(248, 113, 113, 0.3)' : 'transparent'
                                        }}
                                    >
                                        <Tooltip placement="top" title={isReadClicked ? 'Dừng đọc câu trả lời' : 'Đọc câu trả lời'}>
                                            <ReadAloud
                                                className={`cursor-pointer ${
                                                    isReadClicked ? 'fill-white-3000 stroke-red-2000' : 'fill-none stroke-gray-4000'
                                                }`}
                                                onClick={async () => {
                                                    if (isReadClicked) {
                                                        audio.pause();
                                                        setIsReadClicked(false);
                                                    } else {
                                                        setIsReadClicked(true);
                                                        const new_audio = new Audio(
                                                            `${apiHost}/api/t2s/vits?answer=${message}&api_key=qLQuunfY0ess1LFmup6tBJDwXFka5Ez1SPBH2NHXeo`
                                                        );
                                                        setAudio(new_audio);
                                                        new_audio.play();
                                                        new_audio.addEventListener('ended', () => setIsReadClicked(false));
                                                    }
                                                }}
                                                onMouseEnter={() => setIsReadHovered(true)}
                                                onMouseLeave={() => setIsReadHovered(false)}
                                            />
                                        </Tooltip>
                                    </div>
                                </div>
                            </>
                        )}
                    </div>
                </div>

                {sources && openSource !== null && sources[openSource] && (
                    <div className="ml-10 mt-2 max-w-4xl rounded-xl bg-blue-200 p-2">
                        <p className="m-1 w-3/4 truncate text-xs text-gray-500">Source: {sources[openSource].title}</p>

                        <div className="m-2 rounded-xl border-2 border-gray-200 bg-white p-2">
                            <p className="text-black">{sources[openSource].text}</p>
                        </div>
                    </div>
                )}
            </div>
        );
    }
    return bubble;
});

export default ConversationBubble;
